import { Vector2, Vector3 } from 'three';
import { PenIcon } from '../../Assets';
import { computePick, projectToScreen } from '../../Utils';
import { Service } from '../Service';
import { Editor } from './Editor';
/**
 * API for drawing 3D annotations in the scene.
 */
export class DrawService extends Service {
    constructor(modules, navigationService) {
        super(modules);
        Object.defineProperty(this, "camera", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigationService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "editor", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "dragging", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "modifier", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "snapped", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "selectedVertex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "contextMenuHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onSubmitCallback", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.camera = this.renderer.getWorld().getCamera();
        this.navigationService = navigationService;
        this.editor = new Editor(this.renderer, this.labelManager);
        this.dragging = false;
        this.modifier = false;
        this.snapped = false;
        this.selectedVertex = null;
        this.contextMenuHandler = () => { };
        this.onSubmitCallback = () => { };
    }
    /**
     * Update the context menu.
     */
    updateContextMenu() {
        const { x, y } = this.renderer.getSize();
        const viewportSize = new Vector2(x, y);
        const vertices = this.editor.getVertices();
        if (vertices.length) {
            let target = vertices[vertices.length - 1];
            if (this.selectedVertex) {
                target = this.selectedVertex;
            }
            const targetPosition = new Vector3();
            target.getPositionECEF(targetPosition);
            this.contextMenuHandler(projectToScreen(this.camera, viewportSize, targetPosition), () => {
                this.editor.removeVertex(target);
                this.updateContextMenu();
                this.gestures.focus();
            });
        }
        else {
            this.contextMenuHandler(null, () => { });
        }
    }
    /**
     * Start a new sketch.
     *
     * @param vertices
     * @param closed
     */
    async start(vertices = [], closed = false) {
        // Stop current sketch, if any
        this.stop();
        // Add initial set of vertices (e.g., editing an existing annotation)
        this.gestures.setCursor(`url(${PenIcon}), pointer`);
        for (const vertex of vertices) {
            await this.editor.addVertex(vertex);
        }
        if (vertices.length > 0) {
            this.editor.setCursor(vertices[vertices.length - 1]);
            if (closed) {
                this.editor.closePolygon();
            }
            else {
                this.editor.openPolygon();
            }
        }
        // Handle keyboard shortcuts for enabling the modifier, cancelling, and undo
        this.gestures.register('keytap', gesture => {
            const { key } = gesture;
            if (key === 'Meta' || key === 'Control' || key === 'Shift') {
                this.modifier = true;
                this.gestures.setCursor('crosshair');
            }
            if (key === 'C' || key === 'c' || key === 'Escape') {
                this.clear();
            }
            if (key === 'U' || key === 'u') {
                this.undo();
            }
            this.updateContextMenu();
        });
        // Handle disabling modifier
        this.gestures.register('keyrelease', gesture => {
            const { key } = gesture;
            if (key === 'Meta' || key === 'Control' || key === 'Shift') {
                this.modifier = false;
                this.gestures.setCursor(`url(${PenIcon}), pointer`);
            }
        });
        // Handle placing, deleting, and selecting a vertex
        this.gestures.register('tap', gesture => {
            const position = computePick(this.renderer, gesture.position);
            if (gesture.button === 0) {
                if (this.selectedVertex && !this.modifier) {
                    // Select a vertex for dragging
                    this.dragging = true;
                    this.navigationService.disable();
                    this.gestures.setCursor('grabbing');
                }
                else if (this.modifier && this.snapped) {
                    // Close the shape if the modifier is on
                    this.editor.closePolygon();
                }
                else if (!this.editor.isPolygonClosed()) {
                    // Add a new vertex, if not already closed
                    this.editor.addVertex(position);
                }
            }
            else if (gesture.button === 2 && this.selectedVertex) {
                // Delete the selected vertex
                this.editor.removeVertex(this.selectedVertex);
            }
            this.updateContextMenu();
        });
        // Handle releasing a vertex
        this.gestures.register('release', () => {
            if (this.dragging) {
                this.dragging = false;
                this.navigationService.enable();
                this.gestures.setCursor('grab');
            }
        });
        // Handle dragging a vertex or snapping to fill the polygon
        this.gestures.register('hover', gesture => {
            const { x, y } = this.renderer.getSize();
            const pick = this.renderer.pickScreenWorldSpaceSnapping(gesture.position);
            const viewportSize = new Vector2(x, y);
            const position = new Vector3(pick.position.x, pick.position.y, pick.position.z);
            const screenPosition = projectToScreen(this.camera, viewportSize, position);
            const vertices = this.editor.getVertices();
            // Drag / update the selected vertex
            if (this.dragging && this.selectedVertex) {
                this.selectedVertex.setPositionECEF(position);
                this.gestures.setCursor('grabbing');
            }
            else {
                this.selectedVertex = null;
                for (const vertex of vertices) {
                    const vertexPosition = new Vector3();
                    vertex.getPositionECEF(vertexPosition);
                    const pointPosition = projectToScreen(this.camera, viewportSize, vertexPosition);
                    if (screenPosition.distanceTo(pointPosition) < 25) {
                        this.selectedVertex = vertex;
                    }
                }
                if (this.selectedVertex) {
                    this.gestures.setCursor('grab');
                }
                else {
                    this.gestures.setCursor(`url(${PenIcon}), pointer`);
                }
                this.editor.highlightVertex(this.selectedVertex);
            }
            // Snap to the first vertex when modifier is on
            if (this.modifier && vertices.length >= 3) {
                const first = new Vector3();
                vertices[0].getPositionECEF(first);
                const pointPosition = projectToScreen(this.camera, viewportSize, first);
                if (screenPosition.distanceTo(pointPosition) < 50) {
                    position.copy(first);
                    this.snapped = true;
                }
                else {
                    this.snapped = false;
                }
                this.gestures.setCursor('crosshair');
            }
            else {
                this.snapped = false;
            }
            this.editor.setCursor(position);
            this.updateContextMenu();
        });
        // Update the context menu when zooming in
        this.gestures.register('zoom', () => {
            this.updateContextMenu();
        });
    }
    /**
     * Undo the placement of the last vertex.
     */
    undo() {
        const vertices = this.editor.getVertices();
        const targetIndex = vertices.length - 1;
        if (targetIndex >= 0) {
            this.editor.removeVertex(vertices[targetIndex]);
        }
    }
    /**
     * Clear the current sketch, reset all state
     */
    clear() {
        this.editor.clear();
        this.dragging = false;
        this.modifier = false;
        this.snapped = false;
    }
    /**
     * Submit the current sketch.
     */
    submit() {
        // Stop listening for gestures and disable the context menu
        this.editor.highlightVertex(null);
        this.editor.setCursor(null);
        this.gestures.unregisterAll();
        this.gestures.setCursor('default');
        this.contextMenuHandler(null, () => { });
        const vertices = this.editor.getVertices();
        const positions = vertices.map(vertex => {
            const vertexPosition = new Vector3();
            vertex.getPositionECEF(vertexPosition);
            return vertexPosition;
        });
        this.onSubmitCallback(positions, this.editor.isPolygonClosed(), this.editor.getColor());
    }
    /**
     * Stop sketching.
     */
    stop() {
        this.clear();
        this.gestures.unregisterAll();
        this.gestures.setCursor('default');
        this.updateContextMenu();
    }
    /**
     * Get the color of the sketch.
     *
     * @returns
     */
    getColor() {
        return this.editor.getColor();
    }
    /**
     * Check if the polygon can be closed.
     *
     * @returns
     */
    canClosePolygon() {
        return this.editor.canClosePolygon();
    }
    /**
     * Set the context menu handler.
     *
     * @param handler
     */
    setContextMenuHandler(handler) {
        this.contextMenuHandler = handler;
    }
    /**
     * Set the callback for when the drawing is submitted.
     *
     * @param callback
     */
    setSubmitCallback(callback) {
        this.onSubmitCallback = callback;
    }
    /**
     * Set the sketch color.
     *
     * @param color
     */
    setColor(color) {
        this.editor.setColor(color);
    }
    /**
     * Set the measurement unit.
     *
     * @param unit
     */
    setMeasurementUnit(unit) {
        this.editor.setMeasurementUnit(unit);
    }
    /**
     * Close the polygon.
     */
    closePolygon() {
        this.editor.closePolygon();
    }
    /**
     * Open the polygon.
     */
    openPolygon() {
        this.editor.openPolygon();
    }
    updateFrame(deltaTime) { }
}
